import React, { useEffect } from "react";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Collapse, IconButton, Popover, Stack, SxProps, Typography } from "@mui/material";
import { ReactComponent as Logo } from "assets/icons/logo.svg";
import { useModals } from "hooks";
import { useTaskManageActions } from "store";
import { DealBase } from "store/types";

import { EmailPreview } from "./Components/EmailPreview";
import { Filters } from "./Components/Filters";
import { List } from "./Components/List";
import { Pagination } from "./Components/Pagination";
import { PopoverHeader } from "./Components/PopoverHeader";
import { Search } from "./Components/Search";
import { SelectDeal } from "./Components/SelectDeal";
import { Sort } from "./Components/Sort";
import { EmailsContext } from "./context/Email-context";
import { useEmailContextHook } from "./context/Email-context-hook";

interface Props {
  isFullScreenMode: boolean;
  anchorEl: HTMLElement;
}

export enum FilterKind {
  DEAL = "deal",
  ASSOCIATION = "association",
  ATTACHMENT = "attachment"
}
export interface EmailInboxFilter {
  deal: DealBase[];
  association: "Suggested only" | "Associated only" | null;
  attachment: "With attachment" | "Without attachment" | null;
}

export const EmailInboxModal: React.FC<Props> = ({ isFullScreenMode, anchorEl }) => {
  const { getDealsAction } = useTaskManageActions();
  const { emailInboxModal } = useModals();
  const emailsContext = useEmailContextHook({
    isFullScreenMode
  });
  const { isFullScreen, setIsFullScreen, selectedEmails } = emailsContext;

  useEffect(() => {
    getDealsAction();
  }, []);

  return (
    <Popover
      open={Boolean(anchorEl)}
      onClose={() => emailInboxModal(null)}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right"
      }}
      sx={{
        ...(isFullScreen ? FullScreenPopoverStyles : PopoverStyles)
      }}
    >
      <EmailsContext.Provider value={emailsContext}>
        {!isFullScreen ? (
          <Stack direction="column" sx={{ height: "660px" }} gap="20px">
            <Stack direction="column" gap="20px">
              <PopoverHeader />
              <Search />
              <Filters />
              <Collapse in={Boolean(selectedEmails.length)}>
                <Typography variant="body2" color="secondary" sx={{ fontSize: 13, margin: "8px 0 18px 0" }}>
                  {selectedEmails.length} emails selected
                </Typography>
                <SelectDeal />
              </Collapse>
            </Stack>
            <List />
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: "auto" }}>
              <Pagination />
              <Sort />
            </Stack>
          </Stack>
        ) : (
          <Stack sx={{ height: "100vh", width: "100%", overflow: "hidden" }}>
            <Stack sx={{ position: "absolute", top: "70px", left: "10px" }}>
              <IconButton onClick={() => setIsFullScreen(false)} sx={{ padding: "0px" }}>
                <ArrowBackIosIcon sx={{ fontSize: "20px", right: "2px", position: "absolute" }} />
              </IconButton>
            </Stack>

            <Stack direction="row" alignItems="center" gap="20px" sx={{ padding: "0px 50px", minHeight: "50px" }}>
              <Logo style={{ width: "25px", height: "26px" }} />
              <Typography sx={{ fontSize: "16px", color: "#0A3142" }}>Email Inbox</Typography>
            </Stack>

            <Stack sx={{ padding: "20px 50px", height: "100%", width: "100%", backgroundColor: "#F6F8FC" }}>
              <Stack
                direction="row"
                sx={{
                  height: "calc(100vh - 90px)",
                  width: "100%"
                }}
                gap="20px"
              >
                <Stack
                  direction="column"
                  sx={{
                    minWidth: "500px",
                    height: "100%",
                    borderRadius: "10px",
                    padding: "20px 20px 20px 20px",
                    backgroundColor: "white",
                    boxShadow: "0 -1px 5px 1px rgba(0,0,0,.08)"
                  }}
                  gap="20px"
                >
                  <Stack direction="column" gap="20px">
                    <Search />
                    <Filters />
                    <Collapse in={Boolean(selectedEmails.length)}>
                      <Typography variant="body2" color="secondary" sx={{ fontSize: 13, margin: "8px 0 18px 0" }}>
                        {selectedEmails.length} emails selected
                      </Typography>
                      <SelectDeal />
                    </Collapse>
                  </Stack>
                  <List />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: "auto" }}>
                    <Pagination />
                    <Sort />
                  </Stack>
                </Stack>
                <EmailPreview />
              </Stack>
            </Stack>
          </Stack>
        )}
      </EmailsContext.Provider>
    </Popover>
  );
};

const FullScreenPopoverStyles: SxProps = {
  "& .MuiPopover-paper": {
    position: "fixed !important",
    top: "0 !important",
    left: "0 !important",
    width: "100vw !important",
    height: "100vh !important",
    maxWidth: "100vw !important",
    maxHeight: "100vh !important",
    margin: "0 !important",
    borderRadius: "0 !important"
  },
  zIndex: 1200
};

const PopoverStyles: SxProps = {
  ".MuiPopover-paper": {
    backgroundColor: "#ffffff",
    minWidth: "500px",
    minHeight: "700px",
    maxHeight: "700px",
    maxWidth: "500px",
    borderRadius: "8px",
    boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
    padding: "20px 20px 20px 20px",
    display: "flex",
    flexDirection: "column",
    top: "48px !important",
    overflow: "hidden"
  },
  zIndex: 1200
};
